import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff, LogIn, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import AuthPageShell from "../components/auth/AuthPageShell";
import {
  AuthTitle,
  AuthSub,
  Divider,
  Label,
  LabelRow,
  Input,
  PassWrap,
  TogglePass,
  Field,
  ForgotLink,
  Submit,
  Footer,
  FooterLink,
  Err,
} from "../components/auth/authFormStyles";
import { cPath } from "../constants";
import { loginUser } from "../store/actions/authActions";
import useSEO from "../hooks/useSEO";

export default function SignInPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { loading, error, isAuthenticated } = useSelector((s) => s.auth);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);

  const from =
    location.state?.from && typeof location.state.from === "string"
      ? location.state.from
      : null;
  const redirectTarget =
    from && from.startsWith("/") ? from : cPath("dashboard");

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTarget, { replace: true });
  }, [isAuthenticated, navigate, redirectTarget]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    await dispatch(loginUser(email.trim(), password));
  };

  const seo = useSEO({ title: "Sign In", noindex: true, nofollow: true });

  return (
    <>
      {seo}
      <AuthPageShell heroHint={t("auth.signIn.heroHint")}>
      <Divider />
      <AuthTitle>{t("auth.signIn.title")}</AuthTitle>
      <AuthSub>{t("auth.signIn.subtitle")}</AuthSub>
      {error ? (
        <Err role="alert">
          <AlertCircle size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{error}</span>
        </Err>
      ) : null}
      <form onSubmit={onSubmit} noValidate>
        <Field>
          <Label htmlFor="si-email">{t("auth.signIn.email")}</Label>
          <Input
            id="si-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </Field>
        <Field>
          <LabelRow>
            <Label htmlFor="si-pass" style={{ marginBottom: 0 }}>
              {t("auth.signIn.password")}
            </Label>
            <ForgotLink to={cPath("forgot-password")}>{t("auth.signIn.forgotPassword")}</ForgotLink>
          </LabelRow>
          <PassWrap>
            <Input
              id="si-pass"
              type={show ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.signIn.password")}
              style={{ paddingRight: "2.75rem" }}
            />
            <TogglePass
              type="button"
              aria-label={show ? "Hide password" : "Show password"}
              onClick={() => setShow((v) => !v)}
            >
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </TogglePass>
          </PassWrap>
        </Field>
        <Submit type="submit" disabled={loading}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
            <LogIn size={18} strokeWidth={2} aria-hidden />
            {loading ? t("auth.signIn.submitting") : t("auth.signIn.submit")}
          </span>
        </Submit>
      </form>
      <Footer>
        {t("auth.signIn.noAccount")} <FooterLink to={cPath("register")}>{t("auth.signIn.createAccount")}</FooterLink>
      </Footer>
      </AuthPageShell>
    </>
  );
}
